import { APP_INITIALIZER, NgModule } from "@angular/core";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ToastrModule } from "ngx-toastr";

// modules: ngs-translate
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
  TranslatePipe,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

import { PrimeNG, providePrimeNG } from "primeng/config";
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

// Markdown module - using the new v20.0.0 approach
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';

// components and services
import { DirectAccessGuard } from "./direct-access-guard";
import { SharedService } from "./shared/services/shared.service";
import { AuthService } from "./shared/services/auth.service";
import { UiService } from "./shared/services/ui.service";
import { AwsService } from "./shared/services/aws.service";
import { BroshService } from "./shared/services/brosh.service";
import { ValidationPipe } from "./shared/components/ui/validation.pipe";
import { CanDeactivateGuardService } from "./shared/services/can-deactivate-guard.service";
import { DatePipe, DecimalPipe } from "@angular/common";
import { DataService } from "./shared/services/data.service";
import { RouteReuseStrategy } from "@angular/router";
import { CustomRouteReuseStrategy } from "./app-routing.config";
import { UploadService } from "./shared/services/upload.service";
import { ChatService } from "./shared/services/chat.service";
import { UsersService } from "./shared/services/users.service";
import { PhoneAreaCodePipe } from "./shared/components/ui/pipes/phone-area-code.pipe";
import { FormatPhonePipe } from "./shared/components/ui/pipes/format-phone.pipe";
import { ReportsService } from "./shared/services/reports.service";
import { PushNotificationsService } from "./shared/services/push-notifications.service";

// import { provideVideoApis } from "@mintplayer/ng-video-player";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

// Custom preset with our primary color
const CustomAura = definePreset(Aura, {
  semantic: {
    ...Aura.semantic,
    primary: {
      50: '#e6f3ff',
      100: '#cce7ff',
      200: '#99cfff',
      300: '#66b7ff',
      400: '#339fff',
      500: '#0094da', // Main primary color
      600: '#0076b0',
      700: '#005886',
      800: '#003a5c',
      900: '#001c32',
      950: '#000e19'
    },
    // colorScheme: {
    //   light: {
    //     surface: {
    //       0: '#ffffff',
    //       50: '#fafafa',
    //       100: '#f5f5f5',
    //       200: '#eeeeee',
    //       300: '#e0e0e0',
    //       400: '#bdbdbd',
    //       500: '#9e9e9e',
    //       600: '#757575',
    //       700: '#616161',
    //       800: '#424242',
    //       900: '#212121',
    //       950: '#000000'
    //     },
    //     text: {
    //       color: '#212121',
    //       mutedColor: '#757575'
    //     }
    //   },
    //   dark: {
    //     surface: {
    //       0: '#ffffff',
    //       50: '#fafafa',
    //       100: '#f5f5f5',
    //       200: '#eeeeee',
    //       300: '#e0e0e0',
    //       400: '#bdbdbd',
    //       500: '#9e9e9e',
    //       600: '#757575',
    //       700: '#616161',
    //       800: '#424242',
    //       900: '#212121',
    //       950: '#000000'
    //     },
    //     text: {
    //       color: '#212121',
    //       mutedColor: '#757575'
    //     }
    //   }
    //}
  },
  // Override component-specific design tokens
  component: {
    radiobutton: {
      root: {
        checkedBackground: '#0094da', // Use your primary color
        filledBackground: '#0094da'
      }
    }
  }
});

const initializeAppFactory = (primeNG: PrimeNG) => () => {
  primeNG.setConfig({ ripple: false });
};

@NgModule({
  declarations: [],
  imports: [
    HttpClientModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      timeOut: 1500,
      positionClass: 'toast-top-full-width',
      preventDuplicates: true,
      closeButton: false,
      enableHtml: true,
      tapToDismiss: true,
      easing: 'ease-out',
      easeTime: 400,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    DirectAccessGuard,
    SharedService,
    AwsService,
    AuthService,
    UiService,
    DataService,
    BroshService,
    ValidationPipe,
    PhoneAreaCodePipe,
    DecimalPipe,
    FormatPhonePipe,
    CanDeactivateGuardService,
    UploadService,
    DatePipe,
    TranslatePipe,
    ChatService,
    UsersService,
    ReportsService,
    PushNotificationsService,
    TranslateService,
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [PrimeNG],
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: CustomAura, options: {
          darkModeSelector: 'never-dark-mode'
        }
      }
    }),
    // Configure ngx-markdown with breaks enabled to preserve line breaks
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {

          gfm: true,
          breaks: true, // This will preserve line breaks
          pedantic: false,
        },
      },
    }),
    // provideVideoApis(),
  ],
})
export class AppModule { }
